if (!customElements.get('custom-laminate-l-table-builder')) {
  customElements.define(
    'custom-laminate-l-table-builder',
    class CustomLaminateLTableBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.config = JSON.parse(this.querySelector('[data-builder-config]').textContent);
        this.config.variants = [
          ...(this.config.primaryVariants || []),
          ...(this.config.premiumVariants || []),
        ];
        this.mainLengthInput = this.querySelector('[data-main-length]');
        this.mainDepthInput = this.querySelector('[data-main-depth]');
        this.returnLengthInput = this.querySelector('[data-return-length]');
        this.returnDepthInput = this.querySelector('[data-return-depth]');
        this.productCache = new Map();
        this.submitButton = this.querySelector('[data-submit]');

        this.mainLengthInput.value = this.config.dimensions.defaultMainLength;
        this.mainDepthInput.value = this.config.dimensions.defaultMainDepth;
        this.returnLengthInput.value = this.config.dimensions.defaultReturnLength;
        this.returnDepthInput.value = this.config.dimensions.defaultReturnDepth;
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('change', this.onInput.bind(this));
        this.submitButton.addEventListener('click', this.addToCart.bind(this));
        this.calculate();
      }

      onInput() {
        this.querySelectorAll('[data-addon-select][data-quantity-target]').forEach((select) => {
          const quantity = this.querySelector(`[data-select-quantity="${select.dataset.quantityTarget}"]`);
          if (!quantity) return;
          const enabled = Boolean(select.value);
          quantity.disabled = !enabled;
          if (enabled && this.quantity(quantity) < 1) quantity.value = 1;
        });

        const cableSelect = this.querySelector('[data-cable-addon]');
        if (cableSelect) {
          const enabled = Boolean(cableSelect.value);
          const colour = this.querySelector('[data-cable-colour]');
          const quantity = this.querySelector('[data-cable-quantity]');
          colour.disabled = !enabled;
          quantity.disabled = !enabled;
          if (enabled && this.quantity(quantity) < 1) quantity.value = 1;
        }
        this.calculate();
      }

      numberValue(input) {
        return Number.parseInt(input.value, 10);
      }

      quantity(input) {
        const value = this.numberValue(input);
        return Number.isFinite(value) && value > 0 ? value : 0;
      }

      selected(name) {
        return this.querySelector(`input[name^="ctb-${name}-"]:checked`)?.value;
      }

      selectedAddOns() {
        const quantities = new Map();
        const add = (key, quantity = 1) => {
          if (!key || !this.config.addOns[key] || quantity <= 0) return;
          quantities.set(key, (quantities.get(key) || 0) + quantity);
        };

        this.querySelectorAll('[data-addon-select]').forEach((select) => {
          const target = select.dataset.quantityTarget;
          const quantity = target
            ? this.quantity(this.querySelector(`[data-select-quantity="${target}"]`))
            : 1;
          add(select.value, target ? Math.max(1, quantity) : quantity);
        });
        this.querySelectorAll('[data-addon-checkbox]:checked').forEach((input) => add(input.dataset.addonCheckbox));
        this.querySelectorAll('[data-addon-quantity]').forEach((input) => add(input.dataset.addonQuantity, this.quantity(input)));

        const cableKey = this.querySelector('[data-cable-addon]')?.value;
        const cableQuantity = this.querySelector('[data-cable-quantity]');
        if (cableKey) add(cableKey, Math.max(1, this.quantity(cableQuantity)));

        return Array.from(quantities, ([key, quantity]) => ({
          key,
          quantity,
          colour: key === cableKey ? this.querySelector('[data-cable-colour]').value : null,
          ...this.config.addOns[key],
        }));
      }

      calculate() {
        this.clearError();
        const mainLength = this.numberValue(this.mainLengthInput);
        const mainDepth = this.numberValue(this.mainDepthInput);
        const returnLength = this.numberValue(this.returnLengthInput);
        const returnDepth = this.numberValue(this.returnDepthInput);
        const dimensions = this.config.dimensions;
        const materialKey = this.querySelector('[data-material]').value;
        this.validationMessage = 'Please enter valid whole-centimetre dimensions within the allowed ranges.';

        if (
          !Number.isInteger(mainLength) ||
          !Number.isInteger(mainDepth) ||
          !Number.isInteger(returnLength) ||
          !Number.isInteger(returnDepth) ||
          mainLength < dimensions.mainLengthMin ||
          mainLength > dimensions.mainLengthMax ||
          mainDepth < dimensions.mainDepthMin ||
          mainDepth > dimensions.mainDepthMax ||
          returnLength < dimensions.returnLengthMin ||
          returnLength > dimensions.returnLengthMax ||
          returnDepth < dimensions.returnDepthMin ||
          returnDepth > dimensions.returnDepthMax
        ) {
          this.result = null;
          this.querySelector('[data-area]').textContent = '—';
          this.querySelector('[data-base-price]').textContent = '—';
          this.querySelector('[data-options-price]').textContent = this.money(0);
          this.querySelector('[data-cable-price]').textContent = this.money(0);
          this.querySelector('[data-total-price]').textContent = '—';
          return;
        }

        const frameKey = this.selected('frame');
        const frameColour = this.querySelector('[data-frame-colour]').value;
        const returnOrientation = this.querySelector('[data-return-orientation]').value;
        const material = this.config.materials[materialKey];
        const frame = this.config.frames[frameKey];
        const areaCm2 = (mainLength * mainDepth) + (returnLength * returnDepth) - (mainDepth * returnDepth);
        const areaM2 = areaCm2 / 10000;
        const priceTier = this.config.priceTiers.find((tier) => mainLength <= tier.maxMainLength);
        const basePrice = priceTier.price + frame.price;

        const selectedAddOns = this.selectedAddOns();
        this.validationMessage = '';
        const optionsPrice = selectedAddOns.reduce((total, addOn) => total + addOn.price * addOn.quantity, 0);
        const cablePrice = selectedAddOns
          .filter((addOn) => addOn.colour)
          .reduce((total, addOn) => total + addOn.price * addOn.quantity, 0);
        const customisationsPrice = optionsPrice - cablePrice;

        this.result = {
          mainLength,
          mainDepth,
          returnLength,
          returnDepth,
          returnOrientation,
          areaCm2,
          areaM2,
          materialKey,
          material,
          frameKey,
          frame,
          frameColour,
          priceTier,
          basePrice,
          optionsPrice,
          customisationsPrice,
          cablePrice,
          totalPrice: basePrice + optionsPrice,
          selectedAddOns,
        };

        this.querySelector('[data-area]').textContent = `${areaM2.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} m²`;
        this.querySelector('[data-base-price]').textContent = this.money(basePrice);
        this.querySelector('[data-options-price]').textContent = this.money(customisationsPrice);
        this.querySelector('[data-cable-price]').textContent = this.money(cablePrice);
        this.querySelector('[data-total-price]').textContent = this.money(basePrice + optionsPrice);
      }

      money(amount) {
        return new Intl.NumberFormat('en-SG', {
          style: 'currency',
          currency: this.config.currency || 'SGD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount);
      }

      baseVariant() {
        if (!this.result) return null;
        const targetCents = Math.round(this.result.basePrice * 100);
        return this.config.variants.find((variant) => Number(variant.price) === targetCents && variant.available);
      }

      async addOnVariant(addOn) {
        if (this.productCache.has(addOn.handle)) return this.productCache.get(addOn.handle);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}products/${addOn.handle}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`${addOn.label} product is unavailable. Please check the customisation product import.`);
        const product = await response.json();
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant) throw new Error(`${addOn.label} has no available Shopify variant.`);
        this.productCache.set(addOn.handle, variant);
        return variant;
      }

      async addOnItem(addOn, tableReference) {
        const variant = await this.addOnVariant(addOn);
        return {
          id: variant.id,
          quantity: addOn.quantity,
          properties: {
            'For custom table': tableReference,
            ...(addOn.colour ? { 'Colour': addOn.colour } : {}),
          },
        };
      }

      async addToCart() {
        this.calculate();
        if (!this.result) {
          this.showError(this.validationMessage || 'Please enter valid whole-centimetre dimensions within the allowed ranges.');
          return;
        }

        const baseVariant = this.baseVariant();
        if (!baseVariant) {
          this.showError(`The ${this.money(this.result.basePrice)} price variant is unavailable. Please check the imported price variants.`);
          return;
        }

        const r = this.result;
        const reference = `Main ${r.mainLength} × ${r.mainDepth} cm · Return ${r.returnLength} × ${r.returnDepth} cm · ${r.returnOrientation} · ${r.material.label} · ${r.frame.label} (${r.frameColour})`;
        const properties = {
          'Main length': `${r.mainLength} cm`,
          'Main depth': `${r.mainDepth} cm`,
          'Return length': `${r.returnLength} cm`,
          'Return depth': `${r.returnDepth} cm`,
          'Return orientation': r.returnOrientation,
          'Area': `${r.areaM2.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} m²`,
          'Tabletop material': r.material.label,
          'Table frame': r.frame.label,
          'Frame colour': r.frameColour,
          'Control panel': 'Default type included',
          'Front profile': 'Straight Edge',
          'Standard price tier': r.priceTier.label,
          'Customisation remarks': this.querySelector('[data-remarks]').value.trim() || 'None',
        };
        r.selectedAddOns.forEach((addOn) => {
          const quantityText = addOn.quantity > 1 ? `Qty: ${addOn.quantity}` : 'Yes';
          properties[addOn.label] = addOn.colour ? `${quantityText} · ${addOn.colour}` : quantityText;
        });

        try {
          this.setLoading(true);
          const addOnItems = await Promise.all(r.selectedAddOns.map((addOn) => this.addOnItem(addOn, reference)));
          const items = [
            { id: baseVariant.id, quantity: 1, properties },
            ...addOnItems,
          ];

          const payload = { items };

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this table to cart.');

          window.location.href = window.routes?.cart_url || '/cart';
        } catch (error) {
          this.showError(error.message || 'Unable to add this table to cart. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        this.submitButton.toggleAttribute('aria-disabled', loading);
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        const wrapper = this.querySelector('[data-error]');
        wrapper.hidden = false;
        wrapper.querySelector('.product-form__error-message').textContent = message;
      }

      clearError() {
        const wrapper = this.querySelector('[data-error]');
        wrapper.hidden = true;
        wrapper.querySelector('.product-form__error-message').textContent = '';
      }
    }
  );
}
